import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BadgeCheck, Crown, Heart, Lock, MapPin, Search, Video } from "lucide-react";
import api, { formatApiError } from "../api";
import { useAuth } from "../context/AuthContext";

export default function Discover() {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState(null);
  const [gated, setGated] = useState(false);
  const [ageMin, setAgeMin] = useState(40);
  const [ageMax, setAgeMax] = useState(75);
  const [location, setLocation] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/discover", { params: { age_min: ageMin, age_max: ageMax, location } });
      setProfiles(data.profiles);
      setGated(false);
    } catch (err) {
      if (err?.response?.status === 403) {
        setGated(true);
        setProfiles([]);
      } else {
        toast.error(formatApiError(err));
      }
    }
  }, [ageMin, ageMax, location]);

  useEffect(() => {
    load();
  }, [load]);

  const like = async (p) => {
    try {
      const { data } = await api.post("/likes", { user_id: p.id });
      if (data.matched) {
        toast.success(`It's a match with ${data.matched_with}! Head to Matches to say hello.`);
      } else {
        toast.success(`You liked ${p.name}.`);
      }
      setProfiles((prev) => prev.map((x) => (x.id === p.id ? { ...x, liked: true } : x)));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (gated) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-24 text-center" data-testid="discover-gated-overlay">
        <div className="card-surface p-10 backdrop-blur">
          <span className="mx-auto w-14 h-14 rounded-full bg-[#FEF3C7] border border-[#FDE68A] flex items-center justify-center text-[#92400E]">
            <Lock size={24} />
          </span>
          <h1 className="font-display text-3xl text-stone-900 mt-6">Your safety is our promise</h1>
          <p className="text-stone-600 mt-4 max-w-md mx-auto leading-relaxed">
            Complete your 5-minute live video interview to browse verified profiles and send messages.
            It's how we keep Veritas40+ completely scam-free.
          </p>
          <Link to="/verification" className="btn-accent mt-8" data-testid="discover-gated-verification-prompt-btn">
            <Video size={16} /> Book my video interview
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div>
          <p className="eyebrow mb-2">Discover</p>
          <h1 className="font-display text-3xl sm:text-4xl text-stone-900">Verified members near you</h1>
        </div>
        <div className="card-surface px-5 py-4 flex flex-wrap items-end gap-4" data-testid="discover-filter-bar">
          <div>
            <label className="text-xs font-dm uppercase tracking-wider text-stone-500 block mb-1">Age min</label>
            <input
              type="number"
              min="40"
              max="100"
              value={ageMin}
              onChange={(e) => setAgeMin(e.target.value)}
              className="w-20 rounded-lg border border-[#E7DFD5] px-3 py-1.5 text-sm"
              data-testid="discover-age-min-slider"
            />
          </div>
          <div>
            <label className="text-xs font-dm uppercase tracking-wider text-stone-500 block mb-1">Age max</label>
            <input
              type="number"
              min="40"
              max="100"
              value={ageMax}
              onChange={(e) => setAgeMax(e.target.value)}
              className="w-20 rounded-lg border border-[#E7DFD5] px-3 py-1.5 text-sm"
              data-testid="discover-age-max-slider"
            />
          </div>
          <div>
            <label className="text-xs font-dm uppercase tracking-wider text-stone-500 block mb-1">Location</label>
            <input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="City or state"
              className="w-44 rounded-lg border border-[#E7DFD5] px-3 py-1.5 text-sm"
              data-testid="discover-location-input"
            />
          </div>
          <button onClick={load} className="btn-secondary !px-4 !py-1.5 text-sm" data-testid="discover-search-btn">
            <Search size={14} /> Search
          </button>
        </div>
      </div>

      {profiles === null ? (
        <p className="text-stone-500 py-20 text-center">Loading members…</p>
      ) : profiles.length === 0 ? (
        <div className="card-surface p-12 text-center text-stone-500" data-testid="discover-empty-state">
          No verified members match these filters yet. Try widening the age range or clearing the location.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {profiles.map((p) => (
            <div key={p.id} className="card-surface overflow-hidden group" data-testid={`discover-profile-card-${p.id}`}>
              <div className="relative h-80 overflow-hidden bg-[#F3EFEA]">
                {p.photo_url ? (
                  <img
                    src={p.photo_url}
                    alt={p.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center font-display text-6xl text-stone-300">
                    {p.name?.[0]}
                  </div>
                )}
                <div className="absolute top-3 left-3 flex gap-2">
                  <span className="badge-verified shadow-sm">
                    <BadgeCheck size={12} /> Video Verified
                  </span>
                  {p.membership && p.membership !== "free" && (
                    <span className="text-xs font-semibold tracking-wide uppercase px-2.5 py-1 rounded-full bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A] inline-flex items-center gap-1 shadow-sm">
                      <Crown size={11} /> {p.membership}
                    </span>
                  )}
                </div>
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-xl text-stone-900">
                    {p.name}, {p.age}
                  </h3>
                </div>
                <p className="text-xs text-stone-500 mt-1 flex items-center gap-1">
                  <MapPin size={12} /> {p.location}
                  {p.profession ? ` · ${p.profession}` : ""}
                </p>
                {p.bio && <p className="mt-3 text-sm text-stone-600 leading-relaxed line-clamp-3">{p.bio}</p>}
                {p.interests?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {p.interests.slice(0, 4).map((i) => (
                      <span key={i} className="text-xs bg-[#F3EFEA] text-stone-600 rounded-full px-2.5 py-1">
                        {i}
                      </span>
                    ))}
                  </div>
                )}
                <div className="mt-5 flex gap-3">
                  <button
                    onClick={() => like(p)}
                    disabled={p.liked}
                    className={`flex-1 inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${
                      p.liked
                        ? "bg-[#F3EFEA] text-stone-400 cursor-default"
                        : "bg-[#C58342] text-white hover:bg-[#AD6F32]"
                    }`}
                    data-testid={`discover-profile-card-like-btn-${p.id}`}
                  >
                    <Heart size={15} fill={p.liked ? "currentColor" : "none"} /> {p.liked ? "Liked" : "Like"}
                  </button>
                  <Link
                    to="/matches"
                    className="btn-secondary !px-4 !py-2.5 text-sm"
                    data-testid={`discover-profile-card-message-btn-${p.id}`}
                  >
                    Matches
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
